import { FC } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { t } from 'i18next';
import { Controller, Form, FormProvider } from 'react-hook-form';

import { theme } from '@store';
import { SelectFormField, TextFormField } from 'components/DynamicFormFields';

import {
  CustomModal,
  FormGooglePlacesAutocomplete,
  SaveChangesModal,
  USStateSelect,
} from '@components';
import { useEditEmergencyContactItem } from '@views/pages/Patient/components/CareTeam/components/hooks';
import { isNil } from 'lodash';

interface EditEmergencyContactProps {
  patientId: string;
  disabled: boolean;
  pec_id?: string;
}

export const EditEmergencyContactItem: FC<EditEmergencyContactProps> = ({
  patientId,
  disabled = false,
  pec_id = '',
}) => {
  const {
    isOpen,
    handleClose,
    handleOpen,
    isSubmitting,
    relationshipOptions,
    phoneTypeOptions,
    formMethods,
    onSubmit,
    handleSelectLocation,
    isSaveChangesModalOpen,
    handleCloseSaveChangesModal,
    handleDiscardChanges,
    handleSaveChanges,
    isValid,
    isEditSubmitting,
  } = useEditEmergencyContactItem(patientId, pec_id);

  const { control } = formMethods;

  return (
    <>
      <Button sx={{ height: '30px' }} onClick={handleOpen} disabled={disabled}>
        {t('patient.careTeam.edit')}
      </Button>

      <CustomModal
        isOpen={isOpen && !disabled}
        onClose={handleClose}
        title={t(`patient.careTeam.modal.emergencyContact.title.edit`)}
      >
        <FormProvider {...formMethods}>
          <Form
            control={control}
            onSubmit={e => onSubmit(e.event)}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                gap: '8px',
              }}
            >
              <TextFormField
                name='first_name'
                label={t('patient.careTeam.modal.labels.firstName')}
              />

              <TextFormField
                name='middle_name'
                label={t('patient.careTeam.modal.labels.middleName')}
                optional
              />
            </Box>
            <TextFormField
              name='last_name'
              label={t('patient.careTeam.modal.labels.lastName')}
            />
            <SelectFormField
              name='relationship'
              label={t('patient.careTeam.modal.labels.relationship')}
              options={relationshipOptions}
            />

            <Typography variant='subtitle2'>
              {t('patient.careTeam.modal.emergencyContact.subTitle')}
            </Typography>

            <FormGooglePlacesAutocomplete onChange={handleSelectLocation} />

            <Box
              sx={{
                display: 'flex',
                gap: '8px',
              }}
            >
              <TextFormField
                name='street'
                label={t('patient.careTeam.modal.labels.addressLine', {
                  number: 1,
                })}
                optional
              />

              <TextFormField
                name='suite'
                label={t('patient.careTeam.modal.labels.addressLine', {
                  number: 2,
                })}
                optional
              />
            </Box>

            <Box
              sx={{
                display: 'flex',
                gap: '8px',
              }}
            >
              <Controller
                name='state'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <USStateSelect
                    {...field}
                    error={!isNil(error)}
                    helperText={error ? error.message : ''}
                  />
                )}
              />

              <TextFormField
                name='city'
                label={t('patient.careTeam.modal.labels.city')}
                optional
              />
            </Box>

            <TextFormField
              name='zip_code'
              label={t('patient.careTeam.modal.labels.zip')}
              optional
            />
            <TextFormField
              name='country'
              label={t('patient.careTeam.modal.labels.country')}
              optional
            />
            <TextFormField
              name='email'
              label={t('patient.careTeam.modal.labels.email')}
              optional
            />

            <Box
              sx={{
                display: 'flex',
                gap: '8px',
              }}
            >
              <TextFormField
                name='phone_number'
                label={t('patient.careTeam.modal.labels.phone')}
              />

              <SelectFormField
                name='phone_type'
                label={t('patient.careTeam.modal.labels.phoneType')}
                options={phoneTypeOptions}
              />
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '8px',
                position: 'sticky',
                bottom: 0,
                paddingY: 3,
                backgroundColor: theme.palette.common.white,
                zIndex: 3,
              }}
            >
              <Button
                variant='text'
                sx={{
                  fontSize: '0,875rem',
                  fontWeight: 'medium',
                  lineHeight: '24px',
                }}
                disabled={isSubmitting}
                onClick={handleClose}
              >
                {t('general.cancel')}
              </Button>
              <Button
                type='submit'
                variant='contained'
                color='secondary'
                sx={{
                  fontSize: '0,875rem',
                  fontWeight: 'medium',
                  lineHeight: '24px',
                  color: 'white',
                }}
                disabled={isSubmitting || isEditSubmitting}
                loading={isEditSubmitting}
                loadingPosition='start'
              >
                {t(`general.${isEditSubmitting ? 'saving' : 'save'}`)}
              </Button>
            </Box>
            <SaveChangesModal
              isOpen={isSaveChangesModalOpen}
              onClose={handleCloseSaveChangesModal}
              isValid={isValid}
              handleSaveChanges={handleSaveChanges}
              discardChanges={handleDiscardChanges}
              isLoading={isEditSubmitting}
            />
          </Form>
        </FormProvider>
      </CustomModal>
    </>
  );
};
